import { Card } from "@/components/ui/card";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Header } from "@/components/Header";
import { AnimatedSection } from "@/components/AnimatedSection";
import { TeamMemberCard } from "@/components/TeamMemberCard";
import { Users } from "lucide-react";

interface TeamMember {
  id: number;
  name: string;
  title: string;
  role: string;
  bio: string;
  image: string;
  contact: string;
  qrCodeImage?: string;
  linkedinUrl?: string;
}

const Equipe = () => {
  const { toast } = useToast();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([
    {
      id: 5,
      name: "Denis Queiroz",
      title: "Profissional",
      role: "Desenvolvedor",
      bio: "Olá! Sou um profissional apaixonado por tecnologia da informação com experiência em infraestrutura e suporte de TI. Ao longo de minha carreira, tive a oportunidade de trabalhar em diversas empresas renomadas, onde pude desenvolver e aprimorar minhas habilidades técnicas e de liderança.",
      image: "/lovable-uploads/e28f223c-6bc5-4f36-b895-430735dccfd0.png",
      contact: "",
      qrCodeImage: "/lovable-uploads/e48071aa-dba4-46b3-8d68-72228c240e74.png",
      linkedinUrl: "https://www.linkedin.com/in/denis-queiroz-078490117/"
    },
    {
      id: 1,
      name: "Fabiano Alves de Lima",
      title: "Engenheiro Hídrico",
      role: "Fundador da AHQ ENGENHARIA LTDA",
      bio: "Engenheiro Hídrico formado pela UNIFEI (2007). Fundador da AHQ ENGENHARIA LTDA. Especializado em recursos hídricos voltados para o setor elétrico.",
      image: "/lovable-uploads/ab6600fc-fa87-43ea-93a7-be5954410309.png",
      contact: "(11) 98616-2105",
      qrCodeImage: "/lovable-uploads/9f09d792-1ced-4efd-af4b-626c92be5ffb.png",
      linkedinUrl: "https://linkedin.com/in/fabiano-alves-de-lima-23432435"
    },
    {
      id: 3,
      name: "Kauan Casemiro",
      title: "Tecnologia em Análise e Desenvolvimento de Sistemas",
      role: "Cursando Anhanguera (2021)",
      bio: "Cursando Tecnologia em Análise e Desenvolvimento de Sistemas pela Anhanguera (2021)",
      image: "/lovable-uploads/676d7ff5-29b6-4d87-b508-68710d90e36d.png",
      contact: "(11) 91702-7356",
      qrCodeImage: "/lovable-uploads/33966ab6-04cc-49af-b0b3-70e62331b4a2.png"
    },
    {
      id: 2,
      name: "Luís Gustavo Alves de Lima",
      title: "Engenheiro Ambiental",
      role: "Cofundador da AHQ ENGENHARIA LTDA",
      bio: "Engenheiro Ambiental formado pela UNIFEI (2007). Cofundador da AHQ ENGENHARIA LTDA. MBA em gestão de projetos e saneamento ambiental.",
      image: "/lovable-uploads/2dab84c5-2c95-44e2-968d-e36452183cbc.png",
      contact: "(12) 98826-7109",
      qrCodeImage: "/lovable-uploads/5b04de97-a4f9-451a-b03d-09ca4759eec9.png",
      linkedinUrl: "https://linkedin.com/in/luis-gustavo-lima-92491133"
    },
  ]);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editText, setEditText] = useState("");

  const handleEdit = (member: TeamMember, field: string) => {
    setEditingId(member.id);
    setEditingField(field);
    setEditText(member[field as keyof TeamMember] as string);
  };

  const handleSave = (id: number, field: string) => {
    setTeamMembers(members =>
      members.map(member =>
        member.id === id ? { ...member, [field]: editText } : member
      )
    );
    setEditingId(null);
    setEditingField(null);
    toast({
      title: "Atualizado com sucesso",
      description: "As alterações foram salvas.",
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setTeamMembers(members =>
          members.map(member =>
            member.id === id ? { ...member, image: reader.result as string } : member
          )
        );
      };
      reader.readAsDataURL(file);
    }
  };

  const handleQRCodeUpload = async (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setTeamMembers(members =>
          members.map(member =>
            member.id === id ? { ...member, qrCodeImage: reader.result as string } : member
          )
        );
        toast({
          title: "QR Code atualizado",
          description: "O QR Code foi atualizado com sucesso.",
        });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen bg-blue-600 relative overflow-x-hidden">
      <Header />
      
      <div className="pt-20 sm:pt-24">
        {/* Hero Section with Gradient */}
        <section className="relative w-full">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950"></div>
          <article className="relative z-10 flex flex-col justify-center items-center text-center xl:max-w-5xl gap-4 text-xl text-white mx-auto py-16 px-4 lg:py-24 lg:px-16 xl:px-28">
            <Users className="w-16 h-16 mb-2" />
            <h1 className="text-3xl sm:text-4xl font-bold">Nossa Equipe</h1>
            <p className="max-w-2xl">
              Conheça os profissionais que fazem a diferença na AHQ Consultoria. 
              Uma equipe multidisciplinar dedicada a oferecer as melhores soluções em recursos hídricos.
            </p>
          </article>
        </section>

        {/* Team Members Section */}
        <section className="py-10 sm:py-16 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <AnimatedSection delay={100}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                {teamMembers.map(member => (
                  <Card key={member.id} className="hover:shadow-lg transition-all duration-300 hover:scale-[1.02] h-full flex flex-col">
                    <TeamMemberCard
                      member={member}
                      editingId={editingId}
                      editingField={editingField}
                      editText={editText}
                      onEdit={handleEdit}
                      onSave={handleSave}
                      onEditText={setEditText}
                      onImageUpload={handleImageUpload}
                      onQRCodeUpload={handleQRCodeUpload}
                    />
                  </Card>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Equipe;
